import {Avatar, Button, Card, IconButton} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetEmployeeWorkProfessionsListQuery, useGetUsersQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {getActiveOptions} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

import {RecordAuditMemberEditDropdown} from './RecordAuditMemberEditDropdown';
import {RecordAuditTeamMember} from './RecordAuditTeamMember';

export interface TeamParticipant {
  employeeId: string;
  professionId: string;
}

interface AddParticipantPlaceholderProps extends TestIdProps {
  onClick: () => void;
}

// Empty-state affordance for the participants list: a blue dashed tile matching a
// RecordAuditTeamMember card — same padding + border, and a 48px content row (the
// member's Flag+name block is taller than its 40px avatar) so the heights line up.
// Mirrors the header snippet placeholder / photo upload card rather than a bare
// "No data" status.
const PLACEHOLDER_CONTENT_HEIGHT = 12;

function AddParticipantPlaceholder(props: AddParticipantPlaceholderProps) {
  return (
    <Box
      padding={3}
      borderRadius="small"
      border="1px dashed"
      borderColor="palettes.blue.60.100"
      backgroundColor="palettes.blue.10.100"
      data-testid={suffixTestId('placeholder', props)}
    >
      <HStack spacing={3} align="center" minHeight={PLACEHOLDER_CONTENT_HEIGHT}>
        <Avatar icon="social/person_add" size="default" />
        <Button
          variant="link"
          title={i18n.t('page.checklistAudits.create.actions.addParticipant')}
          onClick={props.onClick}
          data-testid={suffixTestId('placeholder-button', props)}
        />
      </HStack>
    </Box>
  );
}

interface RecordAuditParticipationProps extends TestIdProps {
  auditorEmployeeId: string;
  responsiblePersonEmployeeId: string | null;
  participants: ReadonlyArray<TeamParticipant>;
  isEditable: boolean;
  onAddParticipant: () => void;
  onRemoveParticipant: (index: number) => void;
  onChangeAuditor: (auditorEmployeeId: string) => void;
  onChangeResponsiblePerson: (responsiblePersonEmployeeId: string | null) => void;
}

// Sentinel option in the responsible-person dropdown that clears the selection.
const CLEAR_RESPONSIBLE_PERSON_VALUE = '';

export function RecordAuditParticipation(props: RecordAuditParticipationProps) {
  const {professionLabelById} = useGetEmployeeWorkProfessionsListQuery(undefined, {
    selectFromResult: ({data}) => ({
      professionLabelById: new Map(
        getActiveOptions(data?.professions).map((option) => [option.value, option.label ?? ''])
      ),
    }),
  });

  // Names are resolved from the user list by id, the same source as the employee selectbox.
  const {nameByUserId} = useGetUsersQuery(undefined, {
    selectFromResult: ({data}) => ({
      nameByUserId: new Map(
        (data ?? []).map((user) => [user.id, `${user.firstName} ${user.lastName}`.trim()])
      ),
    }),
  });

  const userOptions = Array.from(nameByUserId, ([value, label]) => ({value, label}));
  const responsiblePersonOptions = [
    {value: CLEAR_RESPONSIBLE_PERSON_VALUE, label: i18n.t('general.actions.clear')},
    ...userOptions,
  ];
  const peopleCount =
    1 + (isNotNil(props.responsiblePersonEmployeeId) ? 1 : 0) + props.participants.length;

  return (
    <Card data-testid={suffixTestId('team', props)}>
      <VStack spacing={4}>
        <HStack justify="space-between" align="center">
          <Heading size={4}>{i18n.t('page.checklistAudits.team.title')}</Heading>
          <HStack spacing={3} align="center">
            <Text size="small" color="secondary">
              {i18n.t('page.checklistAudits.team.peopleCount', {count: peopleCount})}
            </Text>
            {props.isEditable ? (
              <Button
                variant="link"
                leftIcon="social/person_add"
                title={i18n.t('general.actions.add')}
                onClick={props.onAddParticipant}
                data-testid={suffixTestId('team-add', props)}
              />
            ) : null}
          </HStack>
        </HStack>

        <Grid columns={4} spacing={4}>
          <RecordAuditTeamMember
            data-testid={suffixTestId('team-auditor', props)}
            name={nameByUserId.get(props.auditorEmployeeId) ?? ''}
            roleLabel={i18n.t('page.checklistAudits.create.fields.auditor')}
            roleColorScheme="blue"
            action={
              props.isEditable ? (
                <RecordAuditMemberEditDropdown
                  data-testid={suffixTestId('team-auditor', props)}
                  options={userOptions}
                  selectedValue={props.auditorEmployeeId}
                  onSelect={props.onChangeAuditor}
                />
              ) : undefined
            }
          />
          <Show when={isNotNil(props.responsiblePersonEmployeeId) || props.isEditable}>
            <RecordAuditTeamMember
              data-testid={suffixTestId('team-responsiblePerson', props)}
              name={
                isNotNil(props.responsiblePersonEmployeeId)
                  ? (nameByUserId.get(props.responsiblePersonEmployeeId) ?? '')
                  : i18n.t('page.checklistAudits.team.responsibleUnassigned')
              }
              roleLabel={i18n.t('page.checklistAudits.create.fields.responsiblePerson')}
              roleColorScheme="blue"
              action={
                props.isEditable ? (
                  <RecordAuditMemberEditDropdown
                    data-testid={suffixTestId('team-responsiblePerson', props)}
                    options={responsiblePersonOptions}
                    selectedValue={
                      props.responsiblePersonEmployeeId ?? CLEAR_RESPONSIBLE_PERSON_VALUE
                    }
                    onSelect={(value) =>
                      props.onChangeResponsiblePerson(
                        value === CLEAR_RESPONSIBLE_PERSON_VALUE ? null : value
                      )
                    }
                  />
                ) : undefined
              }
            />
          </Show>
        </Grid>

        <Show when={props.participants.length > 0 || props.isEditable}>
          <Text size="small" color="tertiary">
            {i18n.t('page.checklistAudits.create.fields.participants')}
          </Text>
        </Show>
        <Grid columns={4} spacing={4}>
          {props.participants.map((participant, index) => (
            <RecordAuditTeamMember
              key={`${participant.employeeId}-${participant.professionId}`}
              data-testid={suffixTestId(`team-participant-[${index}]`, props)}
              name={nameByUserId.get(participant.employeeId) ?? ''}
              roleLabel={professionLabelById.get(participant.professionId) ?? ''}
              roleColorScheme="neutral"
              action={
                props.isEditable ? (
                  <IconButton
                    icon="action/delete"
                    size="small"
                    onClick={() => props.onRemoveParticipant(index)}
                    data-testid={suffixTestId(`team-participant-[${index}]-remove`, props)}
                  />
                ) : undefined
              }
            />
          ))}
          {props.participants.length === 0 && props.isEditable ? (
            <AddParticipantPlaceholder
              onClick={props.onAddParticipant}
              data-testid={suffixTestId('team-participants-add', props)}
            />
          ) : null}
        </Grid>
      </VStack>
    </Card>
  );
}
